<template>
  <div
    class="alex-chat"
    :style="chatThemeVars"
    :class="{ 'alex-chat--embedded': embedded, 'alex-chat--dragover': isDragOver }"
    @dragenter.prevent="onChatDragEnter"
    @dragover.prevent="onChatDragOver"
    @dragleave.prevent="onChatDragLeave"
    @drop.prevent="onChatDrop"
  >
    <div v-if="isDragOver" class="alex-drop-overlay" aria-live="polite">
      <i class="fa fa-paperclip" aria-hidden="true"></i>
      <span>Drop file anywhere to attach</span>
    </div>
    <div v-if="showSessionControls" class="alex-chat__toolbar">
      <button type="button" class="alex-btn alex-btn--primary" :disabled="isBootstrapping || isSending" @click="createSession">New chat</button>
      <div ref="sessionDropdownRoot" class="alex-toolbar-label alex-session-panel">
        <button
          type="button"
          class="alex-session-trigger"
          :disabled="isBootstrapping || isSending"
          :aria-expanded="sessionDropdownOpen ? 'true' : 'false'"
          aria-haspopup="listbox"
          aria-label="Open Chat History"
          @click.stop="toggleSessionDropdown"
        >
          <span class="alex-session-trigger__text">Open Chat History</span>
          <i
            class="fa fa-chevron-down alex-session-trigger__caret"
            :class="{ 'alex-session-trigger__caret--open': sessionDropdownOpen }"
            aria-hidden="true"
          ></i>
        </button>
        <div
          v-show="sessionDropdownOpen"
          class="alex-session-dropdown"
          role="listbox"
          :aria-label="'Chat sessions'"
          @click.stop
        >
          <div class="alex-session-list" role="presentation">
            <div
              v-for="s in sessions"
              :key="s.in_chat_session_id"
              class="alex-session-row"
              :class="{ 'alex-session-row--active': selectedSessionId === s.in_chat_session_id }"
              role="option"
              :aria-selected="selectedSessionId === s.in_chat_session_id ? 'true' : 'false'"
            >
              <template v-if="editingSessionId === s.in_chat_session_id">
                <input
                  v-model="editTitleDraft"
                  class="alex-session-input"
                  type="text"
                  maxlength="120"
                  :aria-label="'Rename session ' + s.in_chat_session_id"
                  @keydown.enter.prevent="saveSessionRename(s)"
                  @keydown.esc.prevent="cancelSessionRename"
                />
                <button
                  type="button"
                  class="alex-session-icon alex-session-icon--save"
                  title="Save"
                  aria-label="Save session name"
                  @click="saveSessionRename(s)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="alex-session-icon"
                  title="Cancel"
                  aria-label="Cancel rename"
                  @click="cancelSessionRename"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="alex-session-select"
                  :class="{ 'alex-session-select--active': selectedSessionId === s.in_chat_session_id }"
                  @click="pickSession(s)"
                >
                  {{ s.st_session_title }}
                </button>
                <button
                  type="button"
                  class="alex-session-pencil"
                  title="Edit session name"
                  aria-label="Edit session name"
                  @click.stop="startSessionRename(s)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="alex-session-delete"
                  title="Delete session"
                  aria-label="Delete session"
                  @click.stop="deleteSession(s)"
                >
                  <i class="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="stream" class="alex-chat__stream">
      <p v-if="!selectedSessionId" class="alex-chat__hint">
        {{ showSessionControls
          ? "Start a New chat or pick a session. Messages, uploads, and generated JSON all appear here."
          : "Loading Tracey chat session..." }}
      </p>

      <div
        v-for="msg in displayMessages"
        :key="msg.in_chat_message_id"
        :class="messageRowClasses(msg)"
      >
        <template v-if="isUserRole(msg.st_role)">
          <div class="alex-msg-user__sender">{{ chatUserLabel }}</div>
          <div class="alex-msg-user__bubble">{{ formatDisplayMessage(msg) }}</div>
        </template>
        <template v-else>
        <div v-if="isAssistantRole(msg.st_role)" class="alex-msg-ai__sender">
          <svg class="alex-tracey-star" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l2.4 5.6L20 10l-5.6 2.4L12 18l-2.4-5.6L4 10l5.6-2.4L12 2z" />
          </svg>
          <span class="alex-tracey-brand">Tracey</span>
        </div>
        <div v-else class="alex-bubble__role">{{ labelRole(msg.st_role) }}</div>
        <p v-if="messageTraceyIntro(msg)" class="alex-msg-ai__text">{{ messageTraceyIntro(msg) }}</p>
        <div
          v-if="messageFormPreview(msg.st_message)"
          class="alex-card alex-card--inline"
        >
          <div class="alex-card__head">
            <div class="alex-card__head-main">
              <div class="alex-card__title">
                {{ messageFormPreview(msg.st_message).form.title || "Generated Form" }}
              </div>
              <p
                v-if="formPreviewSubtitle(messageFormPreview(msg.st_message))"
                class="alex-card__subtitle"
              >
                {{ formPreviewSubtitle(messageFormPreview(msg.st_message)) }}
              </p>
            </div>
            <div class="alex-card__stats">
              <span class="alex-card__stat alex-card__stat--add">
                ✦ {{ (messageFormPreview(msg.st_message).questions || []).length }} questions
              </span>
              <span
                v-if="Array.isArray(messageFormPreview(msg.st_message).skip_logic) && messageFormPreview(msg.st_message).skip_logic.length > 0"
                class="alex-card__stat alex-card__stat--logic"
              >
                ⟲ {{ messageFormPreview(msg.st_message).skip_logic.length }} skip rule{{ messageFormPreview(msg.st_message).skip_logic.length > 1 ? 's' : '' }}
              </span>
            </div>
          </div>
          <div v-if="isMessagePreviewExpanded(msg)" class="alex-card__details">
            <div class="alex-card__details-in">
              <div
                v-for="(q, idx) in (messageFormPreview(msg.st_message).questions || [])"
                :key="'msg-q-' + msg.in_chat_message_id + '-' + idx"
                class="alex-preview__question"
                :class="questionPreviewRowClass(q)"
              >
                <div class="alex-preview__qtitle">
                  <span
                    v-if="questionSegmentLabel(q)"
                    class="alex-preview__segment"
                    :class="'alex-preview__segment--' + (q.layout_segment || q.skip_logic_role || 'default')"
                  >
                    {{ questionSegmentLabel(q) }}
                  </span>
                  {{ idx + 1 }}. {{ questionTitle(q) }}
                  <span v-if="isQuestionRequired(q)" class="alex-preview__required">Required</span>
                </div>
                <div class="alex-preview__qmeta">
                  Type: {{ questionType(q) }}
                  <span v-if="questionOptionCountLabel(q)"> · {{ questionOptionCountLabel(q) }}</span>
                  <span v-if="!isQuestionRequired(q)" class="alex-preview__optional">Optional</span>
                </div>
              </div>
              <div
                v-if="Array.isArray(messageFormPreview(msg.st_message).skip_logic) && messageFormPreview(msg.st_message).skip_logic.length"
                class="alex-preview__skiplogic"
              >
                <div class="alex-preview__skiplogic-title">
                  <i class="fa fa-random" aria-hidden="true"></i>
                  {{ skipLogicSectionTitle(messageFormPreview(msg.st_message)) }}
                </div>
                <div
                  v-for="(sl, slIdx) in messageFormPreview(msg.st_message).skip_logic"
                  :key="'msg-sl-' + msg.in_chat_message_id + '-' + slIdx"
                  class="alex-preview__skiplogic-rule"
                >
                  {{ formatSkipRuleLine(sl, messageFormPreview(msg.st_message).questions || []) }}
                </div>
              </div>
            </div>
          </div>
          <div class="alex-card__actions">
            <button
              type="button"
              class="alex-cbtn alex-cbtn--toggle"
              @click="toggleMessagePreview(msg)"
            >
              {{ isMessagePreviewExpanded(msg) ? "Hide details" : "Show details" }}
            </button>
            <button
              v-if="canApplyFormMessage(msg)"
              type="button"
              class="alex-cbtn alex-cbtn--apply"
              :disabled="isApplying || isSending || isBootstrapping || messageHasAppliedMappings(msg)"
              @click="applyFormFromMessage(msg)"
            >
              <span
                v-if="isApplying && applyingMessageId === msg.in_chat_message_id"
                class="alex-cbtn__loader"
                aria-hidden="true"
              ></span>
              {{ applyButtonLabelForMessage(msg) }}
            </button>
          </div>
        </div>
        <div v-else-if="isWelcomeMessage(msg)" class="alex-welcome">
          <div class="alex-welcome__greeting alex-msg-ai__text">{{ formatDisplayMessage(msg) }}</div>
          <div class="alex-welcome__divider">Start with</div>
          <div class="alex-welcome__starters">
            <button
              v-for="starter in starterPrompts"
              :key="starter.title"
              type="button"
              class="alex-welcome__starter"
              :disabled="isSending || isBootstrapping"
              @click="useStarterPrompt(starter.prompt)"
            >
              <div class="alex-welcome__sicon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path :d="starter.iconPath" /></svg>
              </div>
              <div class="alex-welcome__smeta">
                <div class="alex-welcome__stitle">{{ starter.title }}</div>
                <div class="alex-welcome__sdesc">{{ starter.description }}</div>
              </div>
            </button>
          </div>
          <div class="alex-welcome__upload">
            <i class="fa fa-paperclip" aria-hidden="true"></i>
            <span>Drag a PDF, Word doc, or spreadsheet here and I’ll turn it into a form.</span>
          </div>
        </div>
        <div v-else-if="isPublishedFormLockedMessage(msg)" class="alex-published-lock">
          <div class="alex-published-lock__tag">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8h-1V6A5 5 0 007 6v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zM9 6a3 3 0 016 0v2H9V6zm3 11a2 2 0 110-4 2 2 0 010 4z"/></svg>
            {{ messagePublishedLockMeta(msg).lock_tag || "Form is live" }}
          </div>
          <p class="alex-msg-ai__text">{{ formatDisplayMessage(msg) }}</p>
          <div class="alex-published-lock__action">
            <p class="alex-published-lock__lead">{{ messagePublishedLockMeta(msg).action_lead }}</p>
            <button
              type="button"
              class="alex-published-lock__primary"
              :disabled="isDuplicatingForm || isSending"
              @click="duplicatePublishedForm(msg)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zm3 4H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11v14z"/></svg>
              {{ isDuplicatingForm ? "Duplicating your form…" : "Duplicate form & save as draft" }}
            </button>
            <button
              type="button"
              class="alex-published-lock__secondary"
              :disabled="isSending || isDuplicatingForm"
              @click="requestSkipLogicOnLiveForm"
            >
              Or update the skip logic here
            </button>
            <div
              v-if="messagePublishedLockMeta(msg).draft_survey_name"
              class="alex-published-lock__draft"
            >
              <div class="alex-published-lock__draft-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
              </div>
              <div>
                <div class="alex-published-lock__draft-name">{{ messagePublishedLockMeta(msg).draft_survey_name }}</div>
                <div class="alex-published-lock__draft-state">Draft, questions unlocked</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="isProcessingNoticeMessage(msg)" class="alex-processing-notice">
          <div
            class="alex-processing-notice__tag"
            :class="{ 'alex-processing-notice__tag--done': isProcessingNoticeComplete(msg) }"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 14.59L17.59 12 16 10.41 11 15.41 8.41 12.83 7 14.24 11 18.24l2 2.35z"/></svg>
            {{ processingNoticeTag(msg) }}
          </div>
          <p class="alex-msg-ai__text">{{ formatDisplayMessage(msg) }}</p>
          <p v-if="isProcessingNoticeComplete(msg)" class="alex-processing-notice__completed">
            Completed — see Tracey's reply below.
          </p>
          <p v-else-if="isPollingForUserMessage(messageProcessingNoticeMeta(msg).after_user_message_id)" class="alex-processing-notice__waiting">
            Still checking for Tracey's reply…
          </p>
        </div>
        <div v-else-if="shouldShowMessageText(msg)" :class="assistantBodyClasses(msg)">
          <div class="alex-msg-ai__text">{{ formatDisplayMessage(msg) }}</div>
        </div>
        </template>
      </div>


      <div
        v-if="isSending"
        ref="typingIndicator"
        class="alex-msg-ai alex-typing"
        role="status"
        aria-live="polite"
        :aria-label="typingStatusText"
      >
        <div class="alex-msg-ai__sender">
          <svg class="alex-tracey-star" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l2.4 5.6L20 10l-5.6 2.4L12 18l-2.4-5.6L4 10l5.6-2.4L12 2z" />
          </svg>
          <span class="alex-tracey-brand">Tracey</span>
        </div>
        <div class="alex-typing__bubble">
          <span class="alex-typing__text">{{ typingStatusText }}</span>
          <span class="alex-typing__dots" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </div>
      </div>
    </div>

    <div class="alex-chat__composer" :class="{ 'alex-chat__composer--disabled': isBootstrapping }">
      <div class="alex-composer-in">
        <div v-if="attachedFileName" class="alex-attached">
          <div class="alex-file-chip">
            <div
              class="alex-file-chip__icon"
              :class="'alex-file-chip__icon--' + attachedFileIconTone"
            >
              {{ attachedFileTypeBadge }}
            </div>
            <div class="alex-file-chip__meta">
              <div class="alex-file-chip__name">{{ attachedFileName }}</div>
              <div class="alex-file-chip__size">{{ attachedFileMetaLine }}</div>
            </div>
            <button
              type="button"
              class="alex-file-chip__remove"
              title="Remove file"
              aria-label="Remove attached file"
              :disabled="isBootstrapping || isSending"
              @click="removeAttachedFile"
            >
              ×
            </button>
          </div>
        </div>

        <div
          class="alex-composer-box"
          :class="{ 'alex-composer-box--drag': isDragOver }"
        >
          <input
            ref="fileInput"
            type="file"
            class="alex-composer-box__file-input"
            :disabled="isBootstrapping || isSending"
            accept=".txt,.csv,.md,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            @change="onFileSelected"
          />
          <button
            type="button"
            class="alex-attach-btn"
            title="Attach a document"
            aria-label="Attach a document"
            :disabled="isBootstrapping || isSending"
            @click="openFilePicker"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M16.5 6v11.5a4 4 0 01-8 0V5a2.5 2.5 0 015 0v10.5a1 1 0 01-2 0V6H10v9.5a2.5 2.5 0 005 0V5a4 4 0 00-8 0v12.5a5.5 5.5 0 0011 0V6h-1.5z"
              />
            </svg>
          </button>
          <textarea
            ref="composerInput"
            v-model="messageInput"
            class="alex-composer-textarea"
            rows="1"
            :placeholder="isBootstrapping ? 'Loading Tracey chat session…' : 'Describe changes or the form you want…'"
            :disabled="isSending || isBootstrapping"
            @input="onComposerInput"
            @keydown.enter.exact.prevent="onComposerEnter"
          ></textarea>
          <button
            type="button"
            class="alex-send-btn"
            :disabled="!selectedSessionId || isSending || isBootstrapping || isProcessingAttachment"
            :title="isSending ? typingStatusText : 'Send message'"
            :aria-label="isSending ? typingStatusText : 'Send message'"
            @click="sendMessage"
          >
            <span v-if="isSending" class="alex-send-btn__loader" aria-hidden="true"></span>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
            </svg>
          </button>
        </div>

        <p class="alex-composer-hint">
          {{ pendingReplyPollActive
            ? "Tracey may still be finishing your last request. Checking automatically…"
            : isPublishedForm
            ? "Questions are locked on live forms. Duplicate to edit them."
            : "Tracey is AI and can make mistakes. Please double-check responses." }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

var { constantCommonConfig } = require("../../../config/config.js");

export default {
  name: "AIFormBuilderChatPanel",
  props: {
    surveyId: {
      type: [String, Number],
      required: true
    },
    /** When true, fills parent height (drawer body) */
    embedded: {
      type: Boolean,
      default: false
    },
    showSessionControls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      sessions: [],
      selectedSessionId: null,
      messages: [],
      messageInput: "",
      generatedRaw: "",
      /** Snapshot of last preview while a new request is in flight */
      sendingPreviewRaw: "",
      selectedFile: null,
      attachedFileName: "",
      attachedFileContent: "",
      attachedFileSizeBytes: 0,
      isProcessingAttachment: false,
      showRawJson: false,
      isSending: false,
      isApplying: false,
      applyingMessageId: null,
      isDuplicatingForm: false,
      vueCommonConfig: constantCommonConfig,
      isBootstrapping: true,
      sendPhaseIndex: 0,
      sendProgressSteps: [],
      sendProgressHasLargeUpload: false,
      sendProgressStartedAt: 0,
      sendProgressMinDurationMs: 12000,
      sendPhaseTimer: null,
      editingSessionId: null,
      editTitleDraft: "",
      sessionDropdownOpen: false,
      isDragOver: false,
      dragDepth: 0,
      expandedMessagePreviewIds: {},
      greetingMessage:
        "Hi! 👋 I'm Tracey. What shall I build? Try \"Create a visitor log form\" or \"Cleaning checklist with a weekday dropdown.\"",
      starterPrompts: [
        {
          title: "Multi-area site visit",
          description: "Areas serviced + gate chain",
          prompt:
            "Build a multi-area site visit checklist: one multi-select \"Areas serviced\", a gate header and task block per area, then shared comments and signatures. Use area_gate_flat layout with gate-chain skip logic (ANY per area, else to the next gate — not End Form mid-chain).",
          iconPath: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        },
        {
          title: "Daily cleaning checklist",
          description: "Areas, tasks, frequencies",
          prompt: "Create a daily cleaning checklist with areas, tasks, and frequencies.",
          iconPath: "M19.36 2.72l1.42 1.42-5.72 5.71c1.07 1.54 1.22 3.39.32 4.59L9.06 8.12c1.2-.9 3.05-.75 4.59.32l5.71-5.72M5.93 17.57c-2.01-2.01-3.24-4.41-3.58-6.65l4.88-2.09 7.44 7.44-2.09 4.88c-2.24-.34-4.64-1.57-6.65-3.58z"
        },
        {
          title: "Vehicle inspection",
          description: "Pre-use checks and defects",
          prompt: "Build a vehicle inspection form with pre-use checks and defect reporting.",
          iconPath: "M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"
        },
        {
          title: "Grounds maintenance report",
          description: "Site visits and service logs",
          prompt: "Create a grounds maintenance report for site visits and service logs.",
          iconPath: "M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.26 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.26-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"
        },
        {
          title: "Site safety audit",
          description: "Hazards, controls, sign-off",
          prompt: "Build a site safety audit form covering hazards, controls, and sign-off.",
          iconPath: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"
        }
      ],
      chatSenderName: "",
      pendingReplyPollTimer: null,
      pendingReplyPollUserMessageId: null,
      pendingReplyPollStartedAt: 0,
      pendingReplyPollIntervalMs: 20000,
      pendingReplyPollMaxMs: 600000
    };
  },
  watch: {
    surveyId(newVal, oldVal) {
      if (String(newVal || "") === String(oldVal || "")) {
        return;
      }
      this.bootstrapSurveySession();
    },
    messages() {
      this.$nextTick(() => this.scrollStreamToEnd());
    },
    generatedRaw() {
      if (!this.isSending) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    isSending(val) {
      if (val) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    sendPhaseIndex() {
      if (this.isSending) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    }
  },
  computed: {
    ...mapGetters("LeftSidebarCreate", ["designall", "all"]),
    isPublishedForm() {
      return (
        this.all &&
        Number(this.all.in_status) === Number(this.vueCommonConfig.SURVEY_PUBLISHED)
      );
    },
    /** Survey design tab colours (button / button text) scoped to Tracey chat. */
    chatThemeVars() {
      const d = this.designall && typeof this.designall === "object" ? this.designall : {};
      const vars = {};
      const buttonColor = this.normalizeThemeHex(d.button_color);
      const buttonTextColor = this.normalizeThemeHex(d.button_text_color);
      if (buttonColor) {
        vars["--buttonLinkColor"] = buttonColor;
      }
      if (buttonTextColor) {
        vars["--buttonTextColor"] = buttonTextColor;
      }
      return vars;
    },
    activeGeneratedRaw() {
      const current = (this.generatedRaw || "").trim();
      if (this.isSending) {
        if (current) {
          return current;
        }
        return (this.sendingPreviewRaw || "").trim();
      }
      return current;
    },
    attachedFileTypeBadge() {
      const ext = this.getFileExtension(this.attachedFileName).toUpperCase();
      if (!ext) {
        return "FILE";
      }
      if (ext === "JPEG") {
        return "JPG";
      }
      return ext.length > 4 ? ext.slice(0, 4) : ext;
    },
    attachedFileIconTone() {
      const ext = this.getFileExtension(this.attachedFileName);
      if (ext === "pdf") {
        return "pdf";
      }
      if (ext === "doc" || ext === "docx") {
        return "doc";
      }
      if (ext === "xls" || ext === "xlsx" || ext === "csv") {
        return "sheet";
      }
      if (ext === "png" || ext === "jpg" || ext === "jpeg") {
        return "image";
      }
      return "text";
    },
    attachedFileMetaLine() {
      const sizeLabel = this.formatFileSize(this.attachedFileSizeBytes);
      if (this.isProcessingAttachment) {
        return sizeLabel + " · processing…";
      }
      if ((this.attachedFileContent || "").trim()) {
        return sizeLabel + " · ready for Tracey";
      }
      return sizeLabel;
    },
    displayMessages() {
      if (!this.selectedSessionId) {
        return this.messages;
      }
      let list = [];
      if (Array.isArray(this.messages) && this.messages.length > 0) {
        list = this.messages;
      } else {
        return [
          {
            in_chat_message_id: "tracey-greeting-" + this.selectedSessionId,
            st_role: "assistant",
            st_message: this.greetingMessage
          }
        ];
      }
      return list;
    },
    generatedParsed() {
      try {
        return JSON.parse(this.activeGeneratedRaw || "{}");
      } catch (e) {
        return null;
      }
    },
    generatedQuestions() {
      if (!this.generatedParsed || !Array.isArray(this.generatedParsed.questions)) {
        return [];
      }
      return this.generatedParsed.questions;
    },
    hasAppliedFormMappings() {
      const parsed = this.generatedParsed;
      if (!parsed) {
        return false;
      }
      return this.previewIsApplied(parsed);
    },
    applyButtonLabel() {
      if (this.isApplying) {
        return "Applying to form…";
      }
      return this.hasAppliedFormMappings ? "Applied" : "Apply to form";
    },
    generatedTraceyMessage() {
      const parsed = this.generatedParsed;
      if (!parsed || typeof parsed !== "object") {
        return "";
      }
      return String(parsed.tracey_message || "").trim();
    },
    generatedFormTitle() {
      if (!this.generatedParsed || !this.generatedParsed.form) {
        return "Generated Form";
      }
      return this.generatedParsed.form.title || "Generated Form";
    },
    generatedQuestionCount() {
      return this.generatedQuestions.length;
    },
    generatedSkipLogic() {
      if (!this.generatedParsed || !Array.isArray(this.generatedParsed.skip_logic)) {
        return [];
      }
      return this.generatedParsed.skip_logic;
    },
    sendPhaseLabel() {
      if (!this.isSending || !Array.isArray(this.sendProgressSteps) || this.sendProgressSteps.length === 0) {
        return "Working on your form";
      }
      const idx = Math.min(this.sendPhaseIndex, this.sendProgressSteps.length - 1);
      const step = this.sendProgressSteps[idx];
      return step && step.label ? step.label : "Working on your form";
    },
    typingStatusText() {
      const label = this.sendPhaseLabel;
      if (!label) {
        return "Working on your form…";
      }
      if (label.indexOf("…") !== -1 || label.indexOf("...") !== -1) {
        return label;
      }
      return label + "…";
    },
    generatedSkipLogicCount() {
      return this.generatedSkipLogic.length;
    },
    chatUserLabel() {
      const name = (this.chatSenderName || "").trim();
      return name || "You";
    },
    pendingReplyPollActive() {
      return this.pendingReplyPollUserMessageId != null;
    }
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClickSessionDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClickSessionDropdown);
    this.stopSendProgress();
    this.stopPendingReplyPolling();
  },
  async created() {
    await this.bootstrapSurveySession();
  },
  methods: {
    normalizeThemeHex(color) {
      if (!color || typeof color !== "string") {
        return "";
      }
      const trimmed = color.trim();
      if (!trimmed) {
        return "";
      }
      return trimmed.charAt(0) === "#" ? trimmed : "#" + trimmed;
    },
    sessionStorageKey() {
      return "tracey:selectedSession:" + String(this.surveyId || "global");
    },
    getStoredSessionId() {
      try {
        const raw = window.localStorage.getItem(this.sessionStorageKey());
        if (!raw) {
          return null;
        }
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      } catch (e) {
        return null;
      }
    },
    setStoredSessionId(sessionId) {
      try {
        if (!sessionId) {
          window.localStorage.removeItem(this.sessionStorageKey());
          return;
        }
        window.localStorage.setItem(this.sessionStorageKey(), String(sessionId));
      } catch (e) {
        /* ignore storage errors */
      }
    },
    async bootstrapSurveySession() {
      this.isBootstrapping = true;
      this.clearLocalChatState();
      this.selectedSessionId = null;
      try {
        await Promise.all([this.loadSessions(), this.loadChatFeatureAccess()]);
        const storedId = this.getStoredSessionId();
        let target = null;
        if (storedId) {
          target = this.sessions.find((s) => s && s.in_chat_session_id === storedId) || null;
        }
        if (!target && this.sessions.length) {
          target = this.sessions[0];
        }
        if (target) {
          await this.selectSession(target);
          return;
        }
        await this.createSession(true);
      } catch (e) {
        console.error("Tracey: failed to bootstrap survey session", e);
        this.$awn.alert("Unable to open chat session. Please refresh and try again.");
      } finally {
        this.isBootstrapping = false;
      }
    },
    bubbleKind(role) {
      const r = (role || "").toLowerCase();
      if (r === "user") return "user";
      if (r === "assistant" || r === "system") return "assistant";
      return "assistant";
    },
    messageRowClasses(msg) {
      if (this.isUserRole(msg.st_role)) {
        return ["alex-msg-user"];
      }
      if (this.isAssistantRole(msg.st_role)) {
        return ["alex-msg-ai"];
      }
      return ["alex-bubble"].concat(this.bubbleClasses(msg));
    },
    assistantBodyClasses(msg) {
      const classes = ["alex-msg-ai__body"];
      if (this.isProcessingNoticeMessage(msg)) {
        classes.push("alex-msg-ai__body--processing");
      } else if (this.isErrorMsg(msg)) {
        classes.push("alex-msg-ai__body--error");
      } else if (this.isGuidanceMsg(msg)) {
        classes.push("alex-msg-ai__body--clarify");
      }
      return classes;
    },
    bubbleClasses(msg) {
      const base = "alex-bubble--" + this.bubbleKind(msg.st_role);
      if (this.isProcessingNoticeMessage(msg)) {
        return [base, "alex-bubble--processing"];
      }
      if (this.isErrorMsg(msg)) {
        return [base, "alex-bubble--error"];
      }
      if (this.isGuidanceMsg(msg)) {
        return [base, "alex-bubble--clarify"];
      }
      return [base];
    },
    isUserRole(role) {
      return String(role || "").toLowerCase() === "user";
    },
    isErrorMsg(msg) {
      if (this.isProcessingNoticeMessage(msg)) {
        return false;
      }
      let meta = msg && msg.st_metadata_json;
      if (meta) {
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch (e) {
            meta = null;
          }
        }
        if (meta && meta.type === "error") {
          return true;
        }
      }
      const raw = String((msg && msg.st_message) || "").trim();
      if (!raw || raw.charAt(0) !== "{") {
        return false;
      }
      try {
        const parsed = JSON.parse(raw);
        return !!(parsed && (parsed.error || parsed.type === "error"));
      } catch (e) {
        return false;
      }
    },
    rebrandTraceyMessage(text) {
      let out = String(text || "");
      out = out.replace(/The AI service/gi, "Tracey");
      out = out.replace(/the AI service/gi, "Tracey");
      out = out.replace(/AI response/gi, "response");
      out = out.replace(/configured for AI/gi, "set up");
      out = out.replace(/Something went wrong while building your form/gi, "Tracey could not build your form right now");
      return out;
    },
    humanizeErrorText(technical) {
      const text = String(technical || "").trim();
      const lower = text.toLowerCase();
      if (!text) {
        return "Tracey could not build your form right now. Please try again in a moment.";
      }
      if (lower.indexOf("overloaded") !== -1 || lower.indexOf("529") !== -1) {
        return "Tracey is busy right now. Please wait a minute and try again.";
      }
      if (lower.indexOf("rate_limit") !== -1 || lower.indexOf("429") !== -1) {
        return "Tracey is receiving too many requests. Please wait a moment and try again.";
      }
      if (
        lower.indexOf("timed out") !== -1 ||
        lower.indexOf("timeout") !== -1 ||
        lower.indexOf("econnaborted") !== -1
      ) {
        return "This took too long to complete. Try again, use a smaller attachment, or split the work (for example: generate the form first, then add skip logic in a second message).";
      }
      if (lower.indexOf("max_tokens") !== -1 || lower.indexOf("truncated") !== -1) {
        return "The form was too large to finish in one step. Try a shorter checklist or generate in smaller parts.";
      }
      if (lower.indexOf("not valid json") !== -1 || lower.indexOf("invalid json") !== -1) {
        return "Tracey could not finish the response. Please try sending your message again.";
      }
      if (lower.indexOf("claude_api_key") !== -1 || lower.indexOf("api key") !== -1) {
        return "Tracey is not set up on this server. Please contact your administrator.";
      }
      return "Tracey could not build your form right now. Please try again in a moment.";
    },
    classifySendFailureKind(err) {
      const status =
        err && err.response && err.response.status ? Number(err.response.status) : 0;
      if (status === 429 || status === 503 || status === 529) {
        return "rate_limit";
      }
      if (status === 502 || status === 504 || status === 408) {
        return "connection_timeout";
      }
      const pieces = [];
      if (err && err.message) {
        pieces.push(String(err.message));
      }
      if (err && err.code) {
        pieces.push(String(err.code));
      }
      if (err && err.response && err.response.data && err.response.data.msg) {
        pieces.push(String(err.response.data.msg));
      }
      const lower = pieces.join(" ").toLowerCase();
      if (
        lower.indexOf("overloaded") !== -1 ||
        lower.indexOf("rate_limit") !== -1 ||
        lower.indexOf(" 429 ") !== -1 ||
        lower.indexOf(" 529 ") !== -1
      ) {
        return "rate_limit";
      }
      if (
        lower.indexOf("timed out") !== -1 ||
        lower.indexOf("timeout") !== -1 ||
        lower.indexOf("econnaborted") !== -1 ||
        lower.indexOf("network error") !== -1 ||
        lower.indexOf(" 502 ") !== -1 ||
        lower.indexOf(" 504 ") !== -1
      ) {
        return "connection_timeout";
      }
      return "error";
    },
    processingNoticeText(noticeKind) {
      if (noticeKind === "rate_limit") {
        return "Tracey is busy right now due to high demand. Please wait a minute and check back — your request may still complete. Avoid sending the same prompt again unless nothing appears.";
      }
      if (noticeKind === "connection_timeout") {
        return "Tracey is still processing your request. The connection timed out, but your form may still be generating. Please check back in a few minutes — avoid sending the same request again unless nothing appears.";
      }
      return "";
    },
    parseMessageMeta(msg) {
      let meta = msg && msg.st_metadata_json;
      if (!meta) {
        return {};
      }
      if (typeof meta === "string") {
        try {
          meta = JSON.parse(meta);
        } catch (e) {
          return {};
        }
      }
      return meta && typeof meta === "object" ? meta : {};
    },
    isProcessingNoticeMessage(msg) {
      return this.messageMetaType(msg) === "processing_notice";
    },
    messageProcessingNoticeMeta(msg) {
      return this.parseMessageMeta(msg);
    },
    isProcessingNoticeComplete(msg) {
      const meta = this.messageProcessingNoticeMeta(msg);
      return String(meta.status || "").toLowerCase() === "completed";
    },
    processingNoticeTag(msg) {
      if (this.isProcessingNoticeComplete(msg)) {
        return "Request completed";
      }
      const kind = String(this.messageProcessingNoticeMeta(msg).notice_kind || "");
      if (kind === "rate_limit") {
        return "Tracey is busy";
      }
      return "Still processing";
    },
    isPollingForUserMessage(userMessageId) {
      const id = parseInt(userMessageId, 10);
      if (!Number.isFinite(id) || id < 1) {
        return false;
      }
      return this.pendingReplyPollUserMessageId === id;
    },
    isSubstantiveAssistantReply(msg) {
      if (!msg || !this.isAssistantRole(msg.st_role)) {
        return false;
      }
      const type = this.messageMetaType(msg);
      if (type === "processing_notice" || type === "error") {
        return false;
      }
      return true;
    },
    getLastUserMessageId() {
      const list = Array.isArray(this.messages) ? this.messages : [];
      for (let i = list.length - 1; i >= 0; i--) {
        const m = list[i];
        if (!this.isUserRole(m && m.st_role)) {
          continue;
        }
        const rawId = String((m && m.in_chat_message_id) || "");
        if (rawId.indexOf("local-") === 0) {
          continue;
        }
        const id = parseInt(rawId, 10);
        if (Number.isFinite(id) && id > 0) {
          return id;
        }
      }
      return null;
    },
    hasSubstantiveReplyAfterUserMessage(userMessageId) {
      const targetId = parseInt(userMessageId, 10);
      if (!Number.isFinite(targetId) || targetId < 1) {
        return false;
      }
      const list = Array.isArray(this.messages) ? this.messages : [];
      let foundUser = false;
      for (let i = 0; i < list.length; i++) {
        const m = list[i];
        if (
          this.isUserRole(m.st_role) &&
          parseInt(m.in_chat_message_id, 10) === targetId
        ) {
          foundUser = true;
          continue;
        }
        if (!foundUser) {
          continue;
        }
        if (this.isSubstantiveAssistantReply(m)) {
          return true;
        }
      }
      return false;
    },
    hasPendingProcessingNoticeForUser(userMessageId) {
      const targetId = parseInt(userMessageId, 10);
      if (!Number.isFinite(targetId) || targetId < 1) {
        return false;
      }
      const list = Array.isArray(this.messages) ? this.messages : [];
      return list.some((m) => {
        const meta = this.parseMessageMeta(m);
        return (
          meta.type === "processing_notice" &&
          String(meta.status || "").toLowerCase() === "pending" &&
          parseInt(meta.after_user_message_id, 10) === targetId
        );
      });
    },
    async refreshSessionMessages() {
      if (!this.selectedSessionId) {
        return [];
      }
      try {
        const res = await axios.post(
          process.env.API_PATH + "/chatbot/history",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: this.selectedSessionId
          }),
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          this.messages = res.data.data || [];
          this.syncGeneratedRawFromSessionMessages();
        }
      } catch (e) {
        /* keep current messages */
      }
      return this.messages;
    },
    async persistStatusNotice(noticeKind, userMessageId, customText) {
      if (!this.selectedSessionId || !userMessageId) {
        return false;
      }
      const payload = Object.assign({}, this.basePayload(), {
        in_chat_session_id: this.selectedSessionId,
        after_user_message_id: userMessageId,
        notice_kind: noticeKind
      });
      const preset = this.processingNoticeText(noticeKind);
      if (customText && String(customText).trim()) {
        payload.message = String(customText).trim();
      } else if (preset) {
        payload.message = preset;
      }
      try {
        const res = await axios.post(
          process.env.API_PATH + "/chatbot/message/processing-notice",
          payload,
          this.authHeaders()
        );
        return !!(res.data && res.data.flag === 1);
      } catch (e) {
        return false;
      }
    },
    async completeProcessingNoticesForUserMessage(userMessageId) {
      if (!this.selectedSessionId || !userMessageId) {
        return;
      }
      try {
        await axios.post(
          process.env.API_PATH + "/chatbot/message/processing-notice/complete",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: this.selectedSessionId,
            after_user_message_id: userMessageId
          }),
          this.authHeaders()
        );
      } catch (e) {
        /* non-fatal */
      }
    },
    stopPendingReplyPolling() {
      if (this.pendingReplyPollTimer) {
        clearInterval(this.pendingReplyPollTimer);
        this.pendingReplyPollTimer = null;
      }
      this.pendingReplyPollUserMessageId = null;
      this.pendingReplyPollStartedAt = 0;
    },
    startPendingReplyPolling(userMessageId) {
      const id = parseInt(userMessageId, 10);
      if (!Number.isFinite(id) || id < 1) {
        return;
      }
      this.stopPendingReplyPolling();
      this.pendingReplyPollUserMessageId = id;
      this.pendingReplyPollStartedAt = Date.now();
      this.pendingReplyPollTimer = setInterval(() => {
        this.pollForPendingReply();
      }, this.pendingReplyPollIntervalMs);
      setTimeout(() => {
        this.pollForPendingReply();
      }, 3000);
    },
    async pollForPendingReply() {
      const userMessageId = this.pendingReplyPollUserMessageId;
      if (!userMessageId) {
        return;
      }
      if (
        this.pendingReplyPollStartedAt > 0 &&
        Date.now() - this.pendingReplyPollStartedAt > this.pendingReplyPollMaxMs
      ) {
        this.stopPendingReplyPolling();
        return;
      }
      await this.refreshSessionMessages();
      if (this.hasSubstantiveReplyAfterUserMessage(userMessageId)) {
        await this.completeProcessingNoticesForUserMessage(userMessageId);
        await this.refreshSessionMessages();
        this.stopPendingReplyPolling();
        this.$nextTick(() => {
          this.ensureAreaGatePreviewExpanded();
          this.scrollStreamToEnd();
        });
      }
    },
    resumePendingReplyPollingIfNeeded() {
      const list = Array.isArray(this.messages) ? this.messages : [];
      let latestPendingUserId = null;
      for (let i = list.length - 1; i >= 0; i--) {
        const meta = this.parseMessageMeta(list[i]);
        if (
          meta.type === "processing_notice" &&
          String(meta.status || "").toLowerCase() === "pending" &&
          parseInt(meta.after_user_message_id, 10) > 0
        ) {
          latestPendingUserId = parseInt(meta.after_user_message_id, 10);
          break;
        }
      }
      if (!latestPendingUserId) {
        return;
      }
      if (this.hasSubstantiveReplyAfterUserMessage(latestPendingUserId)) {
        this.completeProcessingNoticesForUserMessage(latestPendingUserId).then(() => {
          this.refreshSessionMessages();
        });
        return;
      }
      this.startPendingReplyPolling(latestPendingUserId);
    },
    async handleSendFailure(err) {
      const noticeKind = this.classifySendFailureKind(err);
      const recoverable =
        noticeKind === "connection_timeout" || noticeKind === "rate_limit";

      try {
        await this.refreshSessionMessages();
        let userMsgId = this.getLastUserMessageId();

        if (userMsgId && this.hasSubstantiveReplyAfterUserMessage(userMsgId)) {
          await this.completeProcessingNoticesForUserMessage(userMsgId);
          await this.refreshSessionMessages();
          return;
        }

        if (!recoverable) {
          await this.delay(2500);
          await this.refreshSessionMessages();
          userMsgId = this.getLastUserMessageId();
          if (userMsgId && this.hasSubstantiveReplyAfterUserMessage(userMsgId)) {
            await this.completeProcessingNoticesForUserMessage(userMsgId);
            await this.refreshSessionMessages();
            return;
          }
        }

        if (recoverable && userMsgId) {
          if (!this.hasPendingProcessingNoticeForUser(userMsgId)) {
            await this.refreshSessionMessages();
            if (this.hasSubstantiveReplyAfterUserMessage(userMsgId)) {
              await this.completeProcessingNoticesForUserMessage(userMsgId);
              await this.refreshSessionMessages();
              return;
            }
            const saved = await this.persistStatusNotice(noticeKind, userMsgId);
            if (!saved) {
              this.pushLocalAssistantMessage(
                this.processingNoticeText(noticeKind) ||
                  this.resolveSendErrorMessage(null, err),
                "processing_notice",
                { notice_kind: noticeKind, status: "pending", after_user_message_id: userMsgId }
              );
            } else {
              await this.refreshSessionMessages();
            }
          }
          this.startPendingReplyPolling(userMsgId);
          return;
        }

        const errText = this.resolveSendErrorMessage(null, err);
        if (userMsgId) {
          const alreadyHasError = this.messages.some((m) => {
            const meta = this.parseMessageMeta(m);
            return (
              meta.type === "error" &&
              parseInt(meta.after_user_message_id, 10) === userMsgId
            );
          });
          if (!alreadyHasError) {
            await this.persistStatusNotice("error", userMsgId, errText);
            await this.refreshSessionMessages();
          }
        } else {
          this.pushLocalAssistantMessage(errText, "error");
        }
      } catch (syncErr) {
        this.pushLocalAssistantMessage(this.resolveSendErrorMessage(null, err), "error");
      }
    },
    formatDisplayMessage(msg) {
      if (this.isErrorMsg(msg)) {
        const raw = String((msg && msg.st_message) || "").trim();
        if (raw.charAt(0) === "{") {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.error) {
              return this.rebrandTraceyMessage(this.humanizeErrorText(parsed.error));
            }
          } catch (e) {
            /* use plain text below */
          }
        }
        return this.rebrandTraceyMessage(
          raw || "Tracey could not build your form right now. Please try again in a moment."
        );
      }
      return String((msg && msg.st_message) || "");
    },
    resolveSendErrorMessage(res, err) {
      if (res && res.data && res.data.msg) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(res.data.msg));
      }
      if (err && err.response && err.response.data && err.response.data.msg) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(err.response.data.msg));
      }
      const timedOut =
        err &&
        (err.code === "ECONNABORTED" ||
          (err.message && String(err.message).toLowerCase().indexOf("timeout") !== -1));
      if (timedOut) {
        return "This took too long to complete. Try again, use a smaller attachment, or split the work (for example: generate the form first, then add skip logic in a second message).";
      }
      if (err && err.message) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(err.message));
      }
      return "Tracey could not build your form right now. Please try again in a moment.";
    },
    isClarificationMsg(msg) {
      return this.messageMetaType(msg) === "clarification";
    },
    isGuidanceMsg(msg) {
      const type = this.messageMetaType(msg);
      return type === "clarification" || type === "non_form_prompt";
    },
    messageMetaType(msg) {
      let meta = msg && msg.st_metadata_json;
      if (!meta) {
        return "";
      }
      if (typeof meta === "string") {
        try {
          meta = JSON.parse(meta);
        } catch (e) {
          return "";
        }
      }
      return meta && meta.type ? String(meta.type) : "";
    },
    labelRole(role) {
      const r = (role || "").toLowerCase();
      if (r === "user") return this.chatUserLabel;
      if (r === "assistant") return "Tracey";
      return role || "Message";
    },
    isAssistantRole(role) {
      return String(role || "").toLowerCase() === "assistant";
    },
    isPublishedFormLockedMessage(msg) {
      return this.messageMetaType(msg) === "published_form_locked";
    },
    messagePublishedLockMeta(msg) {
      let meta = msg && msg.st_metadata_json;
      if (meta && typeof meta === "string") {
        try {
          meta = JSON.parse(meta);
        } catch (e) {
          meta = {};
        }
      }
      return meta && typeof meta === "object" ? meta : {};
    },
    shouldShowMessageText(msg) {
      if (this.isPublishedFormLockedMessage(msg)) {
        return false;
      }
      if (this.isProcessingNoticeMessage(msg)) {
        return false;
      }
      const raw = msg && msg.st_message;
      if (this.messageFormPreview(raw)) {
        return false;
      }
      if (this.isAssistantRole(msg && msg.st_role)) {
        const text = String(raw || "").trim();
        if (text.charAt(0) === "{") {
          try {
            const parsed = JSON.parse(text);
            if (
              parsed &&
              typeof parsed === "object" &&
              Array.isArray(parsed.questions)
            ) {
              return false;
            }
          } catch (e) {
            /* show plain text fallback */
          }
        }
      }
      return true;
    },
    isWelcomeMessage(msg) {
      if (!msg || !this.isAssistantRole(msg.st_role) || !this.selectedSessionId) {
        return false;
      }
      const expectedId = "tracey-greeting-" + this.selectedSessionId;
      return String(msg.in_chat_message_id || "") === expectedId;
    },
    async duplicatePublishedForm(msg) {
      if (this.isDuplicatingForm || !this.surveyId) {
        return;
      }
      this.isDuplicatingForm = true;
      try {
        const res = await axios.post(
          process.env.API_PATH + "/chatbot/survey/duplicate",
          Object.assign({}, this.basePayload(), {
            in_survey_Id: this.surveyId
          }),
          this.authHeaders()
        );
        if (res.data.flag !== 1 || !res.data.data) {
          this.$awn.alert(res.data.msg || "Could not duplicate this form.");
          return;
        }
        const draft = res.data.data;
        const draftName = String(draft.st_survey_name || "").trim();
        const draftId = String(draft.in_survey_Id || "").trim();
        if (msg && msg.st_metadata_json) {
          let meta = msg.st_metadata_json;
          if (typeof meta === "string") {
            try {
              meta = JSON.parse(meta);
            } catch (e) {
              meta = {};
            }
          }
          meta.draft_survey_name = draftName;
          meta.draft_survey_id = draftId;
          msg.st_metadata_json = meta;
        }
        this.pushLocalAssistantMessage(
          "Done. I copied this form into a new draft. The questions are open there, so tell me what to change.",
          "conversation_reply",
          { draft_survey_name: draftName, draft_survey_id: draftId }
        );
        if (draftId) {
          this.$emit("survey-duplicated", {
            in_survey_Id: draftId,
            st_survey_name: draftName
          });
        }
      } catch (e) {
        this.$awn.alert("Could not duplicate this form. Please try again.");
      } finally {
        this.isDuplicatingForm = false;
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    requestSkipLogicOnLiveForm() {
      if (this.isSending || !this.selectedSessionId) {
        return;
      }
      this.messageInput = "Update the skip logic here";
      this.$nextTick(() => this.sendMessage());
    },
    useStarterPrompt(prompt) {
      const text = String(prompt || "").trim();
      if (
        !text ||
        !this.selectedSessionId ||
        this.isSending ||
        this.isBootstrapping
      ) {
        return;
      }
      this.messageInput = text;
      this.$nextTick(() => {
        this.sendMessage();
      });
    },
    questionTitle(question) {
      if (!question || typeof question !== "object") {
        return "Untitled question";
      }
      return question.title || question.question || "Untitled question";
    },
    questionType(question) {
      if (!question || typeof question !== "object") {
        return "unknown";
      }
      return question.question_type_id || question.control_id || "unknown";
    },
    layoutModeLabel(mode) {
      const key = String(mode || "").toLowerCase();
      const labels = {
        area_gate_flat: "Area gate (flat)",
        area_gate_nested: "Area gate (nested zones)",
        flat_by_day: "Weekday routing",
        area_day: "Area + day",
        frequency_bands: "Frequency bands",
        area_headers_weekday: "Area headers + weekday",
        area_first_legacy: "Area-first legacy",
        weekday_routing: "Weekday routing",
        generic_form: "Generic form"
      };
      return labels[key] || (key ? key.replace(/_/g, " ") : "");
    },
    formPreviewSubtitle(preview) {
      if (!preview || !preview.form) {
        return "";
      }
      return String(preview.form.subtitle || "").trim();
    },
    isAreaGatePreview(preview) {
      const layout = String((preview && preview.layout_mode) || "").toLowerCase();
      const pattern = String(
        (preview && preview.skip_logic_pattern) || ""
      ).toLowerCase();
      return (
        layout.indexOf("area_gate") !== -1 || pattern.indexOf("area_gate") !== -1
      );
    },
    questionSegmentLabel(question) {
      if (!question || typeof question !== "object") {
        return "";
      }
      const role = String(question.skip_logic_role || "").toLowerCase();
      if (role === "formation_gate") {
        return "Zone gate";
      }
      if (role === "area_gate") {
        return "Area gate";
      }
      const segment = String(question.layout_segment || "").toLowerCase();
      if (segment === "formation_gate") {
        return "Zone gate";
      }
      if (segment === "area_gate") {
        return "Area gate";
      }
      if (segment === "area_tasks") {
        return "Tasks";
      }
      if (segment === "footer" || String(question.ir_question_id || "").indexOf("footer_") === 0) {
        return "Footer";
      }
      return "";
    },
    questionPreviewRowClass(question) {
      const segment = String(question.layout_segment || question.skip_logic_role || "").toLowerCase();
      if (!segment) {
        return "";
      }
      return "alex-preview__question--" + segment.replace(/[^a-z0-9_-]/g, "");
    },
    questionOptionCountLabel(question) {
      if (!question || typeof question !== "object") {
        return "";
      }
      const dynamic = question.dynamic_config;
      if (!dynamic || !Array.isArray(dynamic.options)) {
        return "";
      }
      const count = dynamic.options.length;
      if (count < 1) {
        return "";
      }
      return count + " option" + (count === 1 ? "" : "s");
    },
    skipLogicSectionTitle(preview) {
      if (this.isAreaGatePreview(preview)) {
        return "Area gate skip chain";
      }
      return "Skip logic";
    },
    questionLabelAt(questions, ref, allowEnd) {
      const canEnd = allowEnd !== false;
      if (canEnd && (ref === -99 || ref === -2 || ref === "-99" || ref === "-2")) {
        return "End form";
      }
      const idx = parseInt(ref, 10);
      if (isNaN(idx) || !Array.isArray(questions) || !questions[idx]) {
        if (!isNaN(idx) && idx >= 0) {
          return "Q" + (idx + 1);
        }
        return "Q?";
      }
      const title = this.questionTitle(questions[idx]);
      return "Q" + (idx + 1) + " · " + title;
    },
    formatSkipRuleLine(rule, questions) {
      if (!rule || typeof rule !== "object") {
        return "";
      }
      const list = Array.isArray(questions) ? questions : [];
      const role = String(rule.skip_logic_role || "").toLowerCase();
      let roleTag = "";
      if (role === "formation_gate") {
        roleTag = "Zone gate · ";
      } else if (role === "area_gate") {
        roleTag = "Area gate · ";
      }
      const srcLabel = this.questionLabelAt(list, rule.source_question_index, false);
      const parentIdx = rule.parent_question_index;
      const sourceIdx = rule.source_question_index;
      let line = roleTag + "If " + srcLabel;
      if (
        parentIdx != null &&
        parentIdx !== "" &&
        parseInt(parentIdx, 10) !== parseInt(sourceIdx, 10)
      ) {
        line += " (when leaving " + this.questionLabelAt(list, parentIdx, false) + ")";
      }
      line += " is " + (rule.condition || "ANSWERED");
      const answers = Array.isArray(rule.answer_values)
        ? rule.answer_values.filter(function (v) {
            return String(v || "").trim() !== "";
          })
        : [];
      if (answers.length) {
        line +=
          " (" +
          (rule.match_type || "EQUALS") +
          " " +
          (rule.inclusion || "ANY") +
          ": " +
          answers.join(", ") +
          ")";
      }
      line +=
        " → then " +
        this.questionLabelAt(list, rule.then_question_index, true) +
        ", else " +
        this.questionLabelAt(list, rule.else_question_index, true);
      if (rule.is_last_gate) {
        line += " [last gate]";
      }
      return line;
    },
    ensureAreaGatePreviewExpanded() {
      const list = Array.isArray(this.messages) ? this.messages : [];
      const next = Object.assign({}, this.expandedMessagePreviewIds);
      let changed = false;
      for (let i = list.length - 1; i >= 0; i--) {
        const preview = this.messageFormPreview(list[i].st_message);
        if (!preview || !this.isAreaGatePreview(preview)) {
          continue;
        }
        const key = this.previewMessageKey(list[i]);
        if (key && !next[key]) {
          next[key] = true;
          changed = true;
        }
        break;
      }
      if (changed) {
        this.expandedMessagePreviewIds = next;
      }
    },
    isQuestionRequired(question) {
      if (!question || typeof question !== "object") {
        return false;
      }
      if (question.validations && typeof question.validations === "object") {
        if (question.validations.required === true || question.validations.required === "true" || question.validations.required === 1) {
          return true;
        }
      }
      if (Array.isArray(question.validations)) {
        return question.validations.some(function (v) {
          return v === "required" || (v && v.type === "required") || (v && v.rule === "required");
        });
      }
      return false;
    },
    previewMessageKey(msg) {
      if (!msg) {
        return "";
      }
      return String(msg.in_chat_message_id || "");
    },
    isMessagePreviewExpanded(msg) {
      const key = this.previewMessageKey(msg);
      if (!key) {
        return false;
      }
      return !!this.expandedMessagePreviewIds[key];
    },
    toggleMessagePreview(msg) {
      const key = this.previewMessageKey(msg);
      if (!key) {
        return;
      }
      const next = Object.assign({}, this.expandedMessagePreviewIds);
      next[key] = !next[key];
      this.expandedMessagePreviewIds = next;
    },
    latestFormMessageId() {
      const list = Array.isArray(this.messages) ? this.messages : [];
      for (let i = list.length - 1; i >= 0; i--) {
        const m = list[i];
        if (this.isAssistantRole(m.st_role) && this.messageFormPreview(m.st_message)) {
          return m.in_chat_message_id;
        }
      }
      return null;
    },
    canApplyFormMessage(msg) {
      if (!msg || !this.messageFormPreview(msg.st_message)) {
        return false;
      }
      const latestId = this.latestFormMessageId();
      return latestId != null && String(latestId) === String(msg.in_chat_message_id);
    },
    previewIsApplied(preview) {
      if (!preview || typeof preview !== "object") {
        return false;
      }
      if (preview.can_apply === true && preview.is_applied !== true && !preview.apply_synced) {
        return false;
      }
      return preview.is_applied === true || preview.apply_synced === true;
    },
    messageHasAppliedMappings(msg) {
      const preview = this.messageFormPreview(msg && msg.st_message);
      return this.previewIsApplied(preview);
    },
    applyButtonLabelForMessage(msg) {
      const applyingThis =
        this.isApplying && String(this.applyingMessageId) === String(msg.in_chat_message_id);
      if (applyingThis) {
        return "Applying to form…";
      }
      return this.messageHasAppliedMappings(msg) ? "Applied" : "Apply to form";
    },
    formPayloadFromMessage(msg) {
      const preview = this.messageFormPreview(msg && msg.st_message);
      if (!preview) {
        return null;
      }
      const generated = JSON.parse(JSON.stringify(preview));
      if (generated.tracey_message) {
        delete generated.tracey_message;
      }
      return generated;
    },
    updateLocalMessageFormPayload(messageId, formObject) {
      const json = JSON.stringify(formObject, null, 2);
      this.messages = (Array.isArray(this.messages) ? this.messages : []).map((m) => {
        if (String(m.in_chat_message_id) === String(messageId)) {
          return Object.assign({}, m, { st_message: json });
        }
        return m;
      });
      this.generatedRaw = json;
    },
    async applyFormFromMessage(msg) {
      if (this.messageHasAppliedMappings(msg)) {
        return;
      }
      const generated = this.formPayloadFromMessage(msg);
      if (!generated) {
        this.$awn.alert("This message does not contain a form to apply.");
        return;
      }
      await this.applyFormObject(generated, msg.in_chat_message_id);
    },
    messageTraceyIntro(msg) {
      let meta = msg && msg.st_metadata_json;
      if (meta) {
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch (e) {
            meta = null;
          }
        }
        if (meta && meta.tracey_message) {
          return String(meta.tracey_message).trim();
        }
      }
      const preview = this.messageFormPreview(msg && msg.st_message);
      if (preview && preview.tracey_message) {
        return String(preview.tracey_message).trim();
      }
      return "";
    },
    messageFormPreview(rawText) {
      const text = (rawText || "").trim();
      if (!text) {
        return null;
      }
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        const start = text.indexOf("{");
        const end = text.lastIndexOf("}");
        if (start !== -1 && end !== -1 && end > start) {
          try {
            parsed = JSON.parse(text.slice(start, end + 1));
          } catch (e2) {
            parsed = null;
          }
        }
      }
      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.form &&
        Array.isArray(parsed.questions)
      ) {
        return parsed;
      }
      return null;
    },
    scrollStreamToEnd() {
      this.$nextTick(() => {
        const el = this.$refs.stream;
        if (!el) {
          return;
        }
        el.scrollTop = el.scrollHeight;
        if (this.isSending && this.$refs.typingIndicator) {
          this.$refs.typingIndicator.scrollIntoView({ block: "end", behavior: "auto" });
        }
      });
    },
    onDocumentClickSessionDropdown(ev) {
      if (!this.sessionDropdownOpen) {
        return;
      }
      if (this.editingSessionId !== null) {
        return;
      }
      const root = this.$refs.sessionDropdownRoot;
      if (root && root.contains(ev.target)) {
        return;
      }
      this.sessionDropdownOpen = false;
    },
    toggleSessionDropdown() {
      if (this.editingSessionId !== null) {
        return;
      }
      this.sessionDropdownOpen = !this.sessionDropdownOpen;
    },
    closeSessionDropdown() {
      this.sessionDropdownOpen = false;
    },
    pickSession(session) {
      if (this.editingSessionId !== null) {
        return;
      }
      if (session) {
        this.selectSession(session);
        this.closeSessionDropdown();
      }
    },
    startSessionRename(session) {
      this.editingSessionId = session.in_chat_session_id;
      this.editTitleDraft = session.st_session_title || "";
      this.$nextTick(() => {
        const list = this.$el.querySelector(".alex-session-input");
        if (list) list.focus();
      });
    },
    cancelSessionRename() {
      this.editingSessionId = null;
      this.editTitleDraft = "";
    },
    async saveSessionRename(session) {
      const title = (this.editTitleDraft || "").trim();
      if (!title) {
        this.$awn.alert("Session name cannot be empty.");
        return;
      }
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/rename",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: session.in_chat_session_id,
            title: title
          }),
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          this.cancelSessionRename();
          await this.loadSessions();
          this.$awn.success("Session name updated.");
        } else {
          this.$awn.alert(res.data.msg || "Could not rename session.");
        }
      } catch (e) {
        this.$awn.alert("Could not rename session.");
      }
    },
    async deleteSession(session) {
      if (this.editingSessionId !== null) {
        return;
      }
      if (
        !window.confirm(
          "Delete this chat session? Its messages will no longer appear in the list."
        )
      ) {
        return;
      }
      const deletedId = session.in_chat_session_id;
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/delete",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: deletedId
          }),
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          if (this.editingSessionId === deletedId) {
            this.cancelSessionRename();
          }
          await this.loadSessions();
          if (this.selectedSessionId === deletedId) {
            this.setStoredSessionId(null);
            if (this.sessions.length) {
              await this.selectSession(this.sessions[0]);
            } else {
              await this.createSession();
            }
          }
          this.closeSessionDropdown();
          this.$awn.success("Session deleted.");
        } else {
          this.$awn.alert(res.data.msg || "Could not delete session.");
        }
      } catch (e) {
        this.$awn.alert("Could not delete session.");
      }
    },
    authHeaders() {
      return {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$session.get("jwt_token")
        }
      };
    },
    basePayload() {
      return {
        lang: this.$session.get("lang"),
        request_id: this.$session.get("requestId")
      };
    },
    async loadSessions() {
      let res = await axios.post(
        process.env.API_PATH + "/chatbot/session/list",
        Object.assign({}, this.basePayload(), {
          in_survey_Id: this.surveyId
        }),
        this.authHeaders()
      );
      if (res.data.flag === 1) {
        this.sessions = res.data.data || [];
      }
    },
    async loadChatFeatureAccess() {
      try {
        const res = await axios.post(
          process.env.API_PATH + "/chatbot/feature/access",
          this.basePayload(),
          this.authHeaders()
        );
        if (
          res.data &&
          res.data.flag === 1 &&
          res.data.data &&
          res.data.data.st_chat_sender_name != null
        ) {
          const name = String(res.data.data.st_chat_sender_name).trim();
          if (name) {
            this.chatSenderName = name;
          }
        }
      } catch (e) {
        /* keep default "You" */
      }
    },
    clearLocalChatState() {
      this.stopSendProgress();
      this.stopPendingReplyPolling();
      this.isSending = false;
      this.messages = [];
      this.expandedMessagePreviewIds = {};
      this.messageInput = "";
      this.generatedRaw = "";
      this.showRawJson = false;
      this.removeAttachedFile();
    },
    async createSession(forceDuringBootstrap = false) {
      if (this.isBootstrapping && !forceDuringBootstrap) {
        return;
      }
      this.clearLocalChatState();
      let payload = Object.assign({}, this.basePayload(), {
        title: "New chat",
        in_survey_Id: this.surveyId
      });
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/create",
          payload,
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          await this.loadSessions();
          await this.selectSession(res.data.data);
        } else {
          console.error("Tracey: createSession returned", res.data);
          this.$awn.alert(res.data.msg || "Unable to create session.");
        }
      } catch (e) {
        console.error("Tracey: createSession error", e);
        this.$awn.alert("Unable to create session. Check console for details.");
      }
    },
    async selectSession(session) {
      this.selectedSessionId = session.in_chat_session_id;
      this.setStoredSessionId(this.selectedSessionId);
      this.showRawJson = false;
      let res = await axios.post(
        process.env.API_PATH + "/chatbot/history",
        Object.assign({}, this.basePayload(), {
          in_chat_session_id: this.selectedSessionId
        }),
        this.authHeaders()
      );
      this.messages = res.data.flag === 1 ? (res.data.data || []) : [];
      this.syncGeneratedRawFromSessionMessages();
      this.$nextTick(() => {
        this.ensureAreaGatePreviewExpanded();
        this.resumePendingReplyPollingIfNeeded();
      });
    },
    syncGeneratedRawFromSessionMessages() {
      var list = Array.isArray(this.messages) ? this.messages : [];
      for (var i = list.length - 1; i >= 0; i--) {
        var m = list[i];
        if (!m || String(m.st_role || "").toLowerCase() !== "assistant") {
          continue;
        }
        var meta = m.st_metadata_json;
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch (eMeta) {
            meta = null;
          }
        }
        if (
          meta &&
          (meta.type === "non_form_prompt" ||
            meta.type === "gratitude_reply" ||
            meta.type === "conversation_reply" ||
            meta.type === "published_form_locked" ||
            meta.type === "clarification" ||
            meta.type === "processing_notice" ||
            meta.type === "error")
        ) {
          this.generatedRaw = "";
          return;
        }
        var raw = String(m.st_message || "").trim();
        if (!raw || raw.charAt(0) !== "{") {
          this.generatedRaw = "";
          return;
        }
        try {
          var parsed = JSON.parse(raw);
          if (
            parsed &&
            parsed.type === "non_form_prompt"
          ) {
            this.generatedRaw = "";
            return;
          }
          if (parsed && parsed.response_type === "clarification") {
            this.generatedRaw = "";
            return;
          }
          if (
            parsed &&
            (Array.isArray(parsed.questions) ||
              (parsed.form && typeof parsed.form === "object"))
          ) {
            this.generatedRaw = JSON.stringify(parsed, null, 2);
            return;
          }
        } catch (e) {
          this.generatedRaw = "";
          return;
        }
      }
      this.generatedRaw = "";
    },
    isImageFile(fileName) {
      return /\.(png|jpe?g)$/i.test(fileName || "");
    },
    allowedFileExtensions() {
      return [
        "txt",
        "csv",
        "md",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "png",
        "jpg",
        "jpeg"
      ];
    },
    supportedFileTypeLabel() {
      return ".txt, .csv, .md, .pdf, .doc, .docx, .xls, .xlsx, .png, .jpg, .jpeg";
    },
    maxAllowedFileBytes() {
      return 5 * 1024 * 1024;
    },
    getFileExtension(fileName) {
      const n = String(fileName || "").toLowerCase().trim();
      const idx = n.lastIndexOf(".");
      if (idx < 0 || idx === n.length - 1) {
        return "";
      }
      return n.slice(idx + 1);
    },
    isAllowedFileType(fileName) {
      const ext = this.getFileExtension(fileName);
      if (!ext) {
        return false;
      }
      return this.allowedFileExtensions().indexOf(ext) !== -1;
    },
    isBinaryFile(fileName) {
      return /\.(pdf|docx?|xlsx?|png|jpe?g)$/i.test(fileName || "");
    },
    dragHasFiles(ev) {
      if (!ev || !ev.dataTransfer) {
        return false;
      }
      if (ev.dataTransfer.files && ev.dataTransfer.files.length > 0) {
        return true;
      }
      const types = ev.dataTransfer.types;
      if (!types) {
        return false;
      }
      return Array.from(types).indexOf("Files") !== -1;
    },
    canAcceptDropNow() {
      return !this.isBootstrapping && !this.isSending;
    },
    onChatDragEnter(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      if (!this.canAcceptDropNow()) {
        return;
      }
      this.dragDepth += 1;
      this.isDragOver = true;
    },
    onChatDragOver(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      if (ev.dataTransfer) {
        ev.dataTransfer.dropEffect = this.canAcceptDropNow() ? "copy" : "none";
      }
      if (!this.canAcceptDropNow()) {
        return;
      }
      this.isDragOver = true;
    },
    onChatDragLeave(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) {
        this.isDragOver = false;
      }
    },
    onChatDrop(ev) {
      this.dragDepth = 0;
      this.isDragOver = false;
      if (!this.dragHasFiles(ev) || !this.canAcceptDropNow()) {
        return;
      }
      const files = ev.dataTransfer && ev.dataTransfer.files ? ev.dataTransfer.files : [];
      if (!files || files.length < 1) {
        return;
      }
      this.processSelectedFile(files[0]);
    },
    processSelectedFile(file) {
      if (!file) {
        return;
      }
      if ((file.size || 0) > this.maxAllowedFileBytes()) {
        this.$awn.alert("File must be under 5 MB.");
        return;
      }
      if (!this.isAllowedFileType(file.name)) {
        this.$awn.alert(
          "Unsupported file type. Supported file types: " + this.supportedFileTypeLabel()
        );
        return;
      }
      if (!this.selectedSessionId) {
        this.$awn.alert("Start or select a chat first.");
        return;
      }
      this.attachedFileName = file.name;
      this.attachedFileSizeBytes = file.size || 0;
      if (this.isBinaryFile(file.name)) {
        this.isProcessingAttachment = true;
        this.extractFileViaServer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (evt) => {
          this.attachedFileContent = evt.target.result || "";
        };
        reader.onerror = () => {
          this.$awn.alert("Could not read file.");
          this.removeAttachedFile();
        };
        reader.readAsText(file);
      }
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.processSelectedFile(file);
    },
    async extractFileViaServer(file) {
      const form = new FormData();
      form.append("file", file);
      form.append("lang", this.$session.get("lang"));
      form.append("request_id", this.$session.get("requestId"));
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/attachment/ocr",
          form,
          {
            headers: {
              Authorization: this.$session.get("jwt_token"),
              "Content-Type": "multipart/form-data"
            }
          }
        );
        if (res.data.flag === 1 && res.data.data && res.data.data.text) {
          this.attachedFileContent = res.data.data.text;
        } else {
          this.pushLocalAssistantMessage(
            this.humanizeErrorText(res.data.msg || "Tracey could not read your attached file."),
            "error"
          );
          this.removeAttachedFile();
        }
      } catch (e) {
        this.pushLocalAssistantMessage(
          "Tracey could not process your file. Try a .txt, .md, or .csv checklist.",
          "error"
        );
        this.removeAttachedFile();
      } finally {
        this.isProcessingAttachment = false;
      }
    },
    openFilePicker() {
      if (this.isBootstrapping || this.isSending) {
        return;
      }
      if (!this.selectedSessionId) {
        this.$awn.alert("Start or select a chat first.");
        return;
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    formatFileSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) {
        return n + " B";
      }
      if (n < 1024 * 1024) {
        return (n / 1024).toFixed(1).replace(/\.0$/, "") + " KB";
      }
      return (n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "") + " MB";
    },
    onComposerInput() {
      const el = this.$refs.composerInput;
      if (!el) {
        return;
      }
      el.style.height = "auto";
      const max = 100;
      el.style.height = Math.min(el.scrollHeight, max) + "px";
    },
    onComposerEnter(ev) {
      if (ev && ev.shiftKey) {
        return;
      }
      this.sendMessage();
    },
    removeAttachedFile() {
      this.attachedFileName = "";
      this.attachedFileContent = "";
      this.attachedFileSizeBytes = 0;
      this.isProcessingAttachment = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    buildSendProgressSteps(hasAttachment) {
      if (hasAttachment) {
        return [
          { id: "prepare", label: "Reading your document" },
          { id: "upload", label: "Uploading form data" },
          { id: "investigate", label: "Investigating checklist content" },
          { id: "plan", label: "Planning form structure" },
          { id: "generate", label: "Building questions & options" }
        ];
      }
      return [
        { id: "send", label: "Reading your message" },
        { id: "investigate", label: "Investigating requirements" },
        { id: "structure", label: "Generating form structure" },
        { id: "questions", label: "Building questions & options" }
      ];
    },
    startSendProgress(hasAttachment) {
      this.stopSendProgress();
      this.sendProgressStartedAt = Date.now();
      this.sendProgressHasLargeUpload = hasAttachment;
      this.sendProgressSteps = this.buildSendProgressSteps(hasAttachment);
      this.sendPhaseIndex = 0;
      const stepCount = this.sendProgressSteps.length;
      const transitions = Math.max(1, stepCount - 1);
      const intervalMs = Math.max(
        900,
        Math.floor((this.sendProgressMinDurationMs - 2000) / transitions)
      );
      this.sendPhaseTimer = setInterval(() => {
        const maxIdx = this.sendProgressSteps.length - 1;
        if (this.sendPhaseIndex < maxIdx) {
          this.sendPhaseIndex += 1;
          this.$nextTick(() => this.scrollStreamToEnd());
        }
      }, intervalMs);
      this.$nextTick(() => this.scrollStreamToEnd());
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    pushLocalAssistantMessage(text, type = "info", extraMeta) {
      const meta = { type: type };
      if (extraMeta && typeof extraMeta === "object") {
        Object.assign(meta, extraMeta);
      }
      this.messages = Array.isArray(this.messages) ? this.messages.slice() : [];
      this.messages.push({
        in_chat_message_id: "local-" + type + "-" + Date.now(),
        st_role: "assistant",
        st_message: String(text || ""),
        st_metadata_json: meta
      });
    },
    async finishSendProgress(options) {
      const fast = !!(options && options.fast);
      if (this.sendPhaseTimer) {
        clearInterval(this.sendPhaseTimer);
        this.sendPhaseTimer = null;
      }
      const steps = this.sendProgressSteps;
      const maxIdx = steps.length > 0 ? steps.length - 1 : 0;
      const startedAt = this.sendProgressStartedAt || Date.now();
      const minMs = fast ? 0 : this.sendProgressMinDurationMs;

      while (this.sendPhaseIndex < maxIdx) {
        const elapsed = Date.now() - startedAt;
        const remaining = minMs - elapsed;
        if (remaining <= 0) {
          this.sendPhaseIndex = maxIdx;
          break;
        }
        const stepsLeft = maxIdx - this.sendPhaseIndex;
        const waitMs = Math.min(remaining, Math.max(400, Math.floor(remaining / stepsLeft)));
        await this.delay(waitMs);
        if (this.sendPhaseIndex < maxIdx) {
          this.sendPhaseIndex += 1;
          this.$nextTick(() => this.scrollStreamToEnd());
        }
      }

      const elapsed = Date.now() - startedAt;
      const remaining = minMs - elapsed;
      if (remaining > 0) {
        await this.delay(remaining);
      }

      this.stopSendProgress();
    },
    stopSendProgress() {
      if (this.sendPhaseTimer) {
        clearInterval(this.sendPhaseTimer);
        this.sendPhaseTimer = null;
      }
      this.sendPhaseIndex = 0;
      this.sendProgressSteps = [];
      this.sendProgressHasLargeUpload = false;
      this.sendProgressStartedAt = 0;
    },
    async sendMessage() {
      if (!this.selectedSessionId) {
        this.pushLocalAssistantMessage("Please start or select a chat session first.", "error");
        return;
      }
      if (this.isSending) {
        return;
      }
      if (!this.messageInput.trim() && !this.attachedFileContent) {
        this.pushLocalAssistantMessage("Message cannot be blank.", "error");
        return;
      }
      if (this.isProcessingAttachment) {
        this.pushLocalAssistantMessage(
          "Tracey is still reading your file. Wait until processing finishes, then send.",
          "error"
        );
        return;
      }
      if (this.attachedFileName && !(this.attachedFileContent || "").trim()) {
        this.pushLocalAssistantMessage(
          "Tracey could not read your attached file. Re-attach the file or use a .txt, .md, or .csv checklist.",
          "error"
        );
        return;
      }

      const visibleMessage = this.messageInput.trim();
      const attachmentContext = (this.attachedFileContent || "").trim();
      const attachmentFileName = this.attachedFileName || "";
      const hasAttachment = attachmentContext.length > 0;
      const localUserMessage =
        visibleMessage !== ""
          ? visibleMessage
          : attachmentFileName !== ""
            ? "Attached file: " + attachmentFileName
            : "Attached file";
      this.messages = Array.isArray(this.messages) ? this.messages.slice() : [];
      this.messages.push({
        in_chat_message_id: "local-user-" + Date.now(),
        st_role: "user",
        st_message: localUserMessage
      });
      this.messageInput = "";
      this.$nextTick(() => this.onComposerInput());
      if (attachmentContext.length > 0) {
        this.removeAttachedFile();
      }
      this.sendingPreviewRaw = (this.generatedRaw || "").trim();
      this.generatedRaw = "";
      this.showRawJson = false;
      this.isSending = true;
      this.startSendProgress(hasAttachment);
      let pendingGeneratedRaw = null;
      let refreshSessionAfterSend = false;
      try {
        const payload = Object.assign({}, this.basePayload(), {
          in_chat_session_id: this.selectedSessionId,
          in_survey_Id: this.surveyId,
          message: visibleMessage,
          attachment_file_name: attachmentFileName,
          attachment_context: attachmentContext
        });
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/message/send",
          payload,
          Object.assign({}, this.authHeaders(), {
            timeout: 600000
          })
        );
        refreshSessionAfterSend = true;
        if (res.data.flag !== 1) {
          pendingGeneratedRaw = "";
        } else if (
          res.data.data &&
          (res.data.data.type === "non_form_prompt" ||
            res.data.data.type === "gratitude_reply" ||
            res.data.data.type === "conversation_reply" ||
            res.data.data.type === "published_form_locked")
        ) {
          pendingGeneratedRaw = "";
        } else if (
          res.data.data &&
          (res.data.data.type === "clarification" ||
            res.data.data.response_type === "clarification")
        ) {
          pendingGeneratedRaw = "";
        } else if (res.data.data) {
          pendingGeneratedRaw = JSON.stringify(res.data.data, null, 2);
        } else {
          pendingGeneratedRaw = "";
        }
        this.showRawJson = false;
      } catch (e) {
        pendingGeneratedRaw = "";
        await this.handleSendFailure(e);
      } finally {
        if (pendingGeneratedRaw !== null) {
          this.generatedRaw = pendingGeneratedRaw;
        }
        if (refreshSessionAfterSend) {
          await this.loadSessions();
          await this.selectSession({ in_chat_session_id: this.selectedSessionId });
        }
        await this.finishSendProgress({ fast: true });
        this.sendingPreviewRaw = "";
        this.isSending = false;
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    async applyFormObject(generated, messageId) {
      if (this.isApplying) {
        return;
      }
      if (!this.selectedSessionId) {
        this.$awn.alert("No active session found.");
        return;
      }
      if (
        generated.response_type === "clarification" ||
        generated.type === "clarification"
      ) {
        this.$awn.alert("Apply is only available after Tracey generates a form, not for clarification messages.");
        return;
      }
      const surveyMd5 = String(this.surveyId || "").trim().toLowerCase();
      if (!surveyMd5 || !/^[a-f0-9]{32}$/.test(surveyMd5)) {
        this.$awn.alert("This form could not be matched. Refresh the builder page and try Apply again.");
        return;
      }
      const payload = Object.assign({}, this.basePayload(), {
        in_survey_Id: surveyMd5,
        generated_form: generated
      });
      this.isApplying = true;
      this.applyingMessageId = messageId;
      try {
        const res = await axios.post(
          process.env.API_PATH + "/chatbot/form/apply",
          payload,
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          const enriched =
            res.data.data &&
            res.data.data.generated_form &&
            typeof res.data.data.generated_form === "object"
              ? res.data.data.generated_form
              : null;
          let updated = enriched;
          if (!updated) {
            updated = this.mergeApplyMappingsIntoForm(generated, res.data.data);
          }
          updated = this.normalizeAppliedFormState(updated);
          if (messageId != null) {
            this.updateLocalMessageFormPayload(messageId, updated);
          } else {
            this.generatedRaw = JSON.stringify(updated, null, 2);
          }
          this.$awn.success(res.data.msg || "Form updated successfully.");
          this.$emit("applied");
        } else {
          const skipErrors =
            res.data.data &&
            Array.isArray(res.data.data.skip_logic_errors) &&
            res.data.data.skip_logic_errors.length
              ? res.data.data.skip_logic_errors
              : null;
          const patternLabel =
            res.data.data && res.data.data.skip_logic_pattern
              ? this.layoutModeLabel(res.data.data.skip_logic_pattern)
              : "";
          if (res.data.data && res.data.data.type === "published_form_locked") {
            this.$awn.alert(
              res.data.msg || "Questions are locked on live forms. Duplicate to a draft to change questions."
            );
          } else {
            const errBody = skipErrors ? skipErrors.join("\n") : res.data.msg || "Failed to apply generated form.";
            this.$awn.alert(
              patternLabel ? "Layout: " + patternLabel + "\n\n" + errBody : errBody
            );
          }
        }
      } catch (e) {
        this.$awn.alert("Failed to apply generated form. Please try again.");
      } finally {
        this.isApplying = false;
        this.applyingMessageId = null;
      }
    },
    normalizeAppliedFormState(formObject) {
      const parsed = JSON.parse(JSON.stringify(formObject || {}));
      const isPendingPreview =
        parsed.type === "skip_logic_only_preview"
        || parsed.type === "form_edit_preview"
        || parsed.edit_scope === "skip_logic_only";
      if (isPendingPreview && parsed.apply_synced) {
        parsed.is_applied = true;
        parsed.can_apply = false;
        parsed.pending_apply = false;
      }
      return parsed;
    },
    mergeApplyMappingsIntoForm(formObject, applyData) {
      const parsed = JSON.parse(JSON.stringify(formObject || {}));
      if (!applyData || !Array.isArray(applyData.questions) || !Array.isArray(parsed.questions)) {
        return parsed;
      }
      const mappings = [];
      applyData.questions.forEach((row, idx) => {
        if (!parsed.questions[idx] || !row || !row.in_question_master_id) {
          return;
        }
        parsed.questions[idx].in_question_master_id = row.in_question_master_id;
        mappings.push({
          index: idx,
          in_question_master_id: row.in_question_master_id
        });
      });
      parsed.applied_mappings = mappings;
      parsed.apply_synced = true;
      return this.normalizeAppliedFormState(parsed);
    }
  }
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.alex-chat {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  height: 100%;
  background: #f4f6f9;
}

.alex-chat--embedded {
  min-height: 0;
}

.alex-drop-overlay {
  position: absolute;
  inset: 10px;
  z-index: 80;
  border: 2px dashed var(--buttonLinkColor);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.88);
  color: var(--buttonLinkColor);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
}

.alex-chat__toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: var(--headerColor);
  border-bottom: 1px solid #e2e8f0;
}

.alex-toolbar-label {
  flex: 1;
  min-width: 120px;
  margin: 0;
}

.alex-session-panel {
  position: relative;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.alex-session-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  font-size: 13px;
  color: var(--headerTextColor);
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
}

.alex-session-trigger:hover {
  border-color: #94a3b8;
  background: #fafafa;
}

.alex-session-trigger__text {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-session-trigger__caret {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--headerTextColor);
  transition: transform 0.15s ease;
}

.alex-session-trigger__caret--open {
  transform: rotate(180deg);
}

.alex-session-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

.alex-session-list {
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
}

.alex-session-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-bottom: 1px solid #f1f5f9;
}

.alex-session-row:last-child {
  border-bottom: none;
}

.alex-session-row--active {
  background: #f0f9ff;
}

.alex-session-select {
  flex: 1;
  min-width: 0;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: #1e293b;
  padding: 6px 4px;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-session-select:hover {
  background: #f8fafc;
}

.alex-session-select--active {
  font-weight: 600;
  color: #0f172a;
}

.alex-session-pencil,
.alex-session-delete,
.alex-session-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.alex-session-pencil:hover,
.alex-session-icon:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.alex-session-delete:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.alex-session-icon--save {
  color: #15803d;
}

.alex-session-icon--save:hover {
  background: #dcfce7;
  color: #14532d;
}

.alex-session-input {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  padding: 6px 8px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
}

.alex-chat__stream {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.alex-chat__hint {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.45;
}

.alex-typing {
  margin: 0;
}

.alex-typing__bubble {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #f8fafc;
  font-size: 13.5px;
  line-height: 1.45;
  color: #64748b;
}

.alex-typing__text {
  flex: 1;
  min-width: 0;
}

.alex-typing__dots {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.alex-typing__dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--buttonLinkColor, #4f46e5);
  animation: alex-typing-blink 1.3s infinite;
}

.alex-typing__dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.alex-typing__dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes alex-typing-blink {
  0%,
  60%,
  100% {
    opacity: 0.25;
  }
  30% {
    opacity: 1;
  }
}

.alex-bubble {
  align-self: flex-start;
  max-width: 92%;
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

/* User message — matches prototype .msg-user */
.alex-msg-user {
  align-self: flex-end;
  max-width: 78%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.alex-msg-user__sender {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 9px;
  color: var(--buttonLinkColor, #6c3f9e);
  text-align: right;
  line-height: 1.2;
}

.alex-msg-user__bubble {
  background: var(--buttonLinkColor, #6c3f9e);
  color: var(--buttonTextColor, #fff);
  padding: 13px 17px;
  border-radius: 16px 16px 5px 16px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Tracey message — matches prototype .msg-ai */
.alex-msg-ai {
  align-self: flex-start;
  max-width: 92%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.alex-msg-ai__sender {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 9px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

.alex-msg-ai__text {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.alex-msg-ai__body {
  padding: 13px 16px;
  border-radius: 13px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.alex-msg-ai__body--clarify {
  border-color: #c4b5fd;
  background: #f5f3ff;
}

.alex-msg-ai__body--clarify .alex-msg-ai__text {
  color: #334155;
}

.alex-msg-ai__body--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.alex-msg-ai__body--error .alex-msg-ai__text {
  color: #7f1d1d;
}

.alex-bubble--clarify {
  border-color: #c4b5fd;
  background: #f5f3ff;
}

.alex-bubble--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.alex-bubble--error .alex-bubble__role {
  color: #b91c1c;
}

.alex-bubble--error .alex-bubble__text {
  color: #7f1d1d;
}

.alex-bubble--json {
  margin-right: 0;
  background: #fafbfc;
  border-color: #cbd5e1;
}

.alex-bubble__role {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
}

.alex-bubble__role--tracey {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: none;
}

.alex-preview__intro {
  margin: 0 0 10px;
  font-size: 14.5px;
  line-height: 1.6;
  color: #334155;
}

.alex-msg-ai__text + .alex-card--inline {
  margin-top: 14px;
}

.alex-welcome {
  margin-top: 2px;
}

.alex-welcome__divider {
  margin: 12px 0 10px;
  font-size: 11px;
  font-weight: 600;
  color: #8590a3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.alex-welcome__starters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.alex-welcome__starter {
  width: 100%;
  border: 1px solid #d8dde7;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.alex-welcome__starter:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 34%, #d6dbe8);
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 7%, #fff);
  transform: translateY(-1px);
}

.alex-welcome__starter:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.alex-welcome__sicon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 13%, #f7f9fc);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.alex-welcome__sicon svg {
  width: 17px;
  height: 17px;
  fill: var(--buttonLinkColor, #2563c9);
}

.alex-welcome__smeta {
  min-width: 0;
}

.alex-welcome__stitle {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.alex-welcome__sdesc {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
}

.alex-welcome__upload {
  margin-top: 12px;
  border: 1px dashed #d4d9e3;
  border-radius: 10px;
  min-height: 34px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #7a8497;
  font-size: 11px;
  background: #f8fafc;
}

.alex-welcome__upload i {
  font-size: 12px;
  color: #687486;
}

@media (max-width: 640px) {
  .alex-welcome__starters {
    grid-template-columns: 1fr;
  }
}

/* Result card — prototype .card */
.alex-card {
  margin-top: 14px;
  border: 1px solid #ebecf0;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 6px 22px -14px rgba(0, 0, 0, 0.22);
}

.alex-card--inline {
  margin-top: 0;
  width: 100%;
}

.alex-card__head {
  padding: 16px 18px;
  background: #fbfbfc;
  border-bottom: 1px solid #ebecf0;
}

.alex-card__head-main {
  min-width: 0;
}

.alex-card__title {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: #2b2d35;
}

.alex-card__subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
  font-weight: 400;
}

.alex-card__stats {
  display: flex;
  gap: 8px;
  margin-top: 11px;
  flex-wrap: wrap;
}

.alex-card__stat {
  font-size: 11px;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.alex-card__stat--add {
  background: rgba(139, 195, 74, 0.16);
  color: #7cb342;
}

.alex-card__stat--logic {
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 12%, #fff);
  color: var(--buttonLinkColor, #2563c9);
}

.alex-card__details-in {
  padding: 16px 18px;
  border-bottom: 1px solid #ebecf0;
  max-height: min(480px, 55vh);
  overflow-y: auto;
}

.alex-card__actions {
  display: flex;
  gap: 10px;
  padding: 14px 18px;
}

.alex-cbtn {
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid #ebecf0;
  background: #fff;
  color: #2b2d35;
  transition: background 0.15s ease;
}

.alex-cbtn:hover:not(.alex-cbtn--apply) {
  background: #fbfbfc;
}

.alex-cbtn--toggle {
  flex: 1;
  color: var(--buttonLinkColor, #2563c9);
  border-color: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 25%, #ebecf0);
}

.alex-cbtn--toggle:hover {
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 8%, #fff);
}

.alex-cbtn--apply {
  flex: 1;
  background: var(--buttonLinkColor, #2563c9);
  color: var(--buttonTextColor, #fff);
  border-color: var(--buttonLinkColor, #2563c9);
}

.alex-cbtn--apply:hover:not(:disabled) {
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 88%, #000);
  color: var(--buttonTextColor, #fff);
  border-color: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 88%, #000);
}

.alex-cbtn--apply:disabled,
.alex-cbtn--apply:disabled:hover {
  background: var(--buttonLinkColor, #2563c9);
  color: var(--buttonTextColor, #fff);
  border-color: var(--buttonLinkColor, #2563c9);
  opacity: 0.65;
  cursor: not-allowed;
}

.alex-cbtn__loader {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  animation: alex-spin 0.7s linear infinite;
  vertical-align: -2px;
}

.alex-tracey-brand {
  color: var(--buttonLinkColor, #2563c9);
}

.alex-tracey-star {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  fill: var(--buttonLinkColor, #2563c9);
}

.alex-bubble__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  color: #1e293b;
}

.alex-json-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
}

.alex-preview {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
}

.alex-preview__title {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.alex-preview__meta {
  margin: 4px 0 10px;
  font-size: 12px;
  color: #64748b;
}

.alex-preview__questions {
  max-height: 220px;
  overflow-y: auto;
}

.alex-preview__question {
  border-top: 1px solid #eef2f7;
  padding: 8px 0;
}

.alex-preview__question:first-child {
  border-top: none;
  padding-top: 0;
}

.alex-preview__question--area_gate,
.alex-preview__question--formation_gate {
  background: #f5f3ff;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 6px;
}

.alex-preview__question--area_tasks {
  margin-left: 8px;
}

.alex-preview__segment {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 4px;
  vertical-align: middle;
}

.alex-preview__segment--area_gate,
.alex-preview__segment--formation_gate {
  background: #ddd6fe;
  color: #5b21b6;
}

.alex-preview__segment--area_tasks {
  background: #e2e8f0;
  color: #475569;
}

.alex-preview__segment--footer {
  background: #f1f5f9;
  color: #64748b;
}

.alex-preview__qtitle {
  font-size: 13px;
  color: #1e293b;
}

.alex-preview__qmeta {
  margin-top: 2px;
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
}

.alex-preview__required {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 4px;
  text-transform: uppercase;
  vertical-align: middle;
}

.alex-preview__optional {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
}

.alex-preview__skiplogic {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.alex-preview__skiplogic-title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.alex-preview__skiplogic-rule {
  font-size: 12px;
  color: #475569;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid color-mix(in srgb, var(--buttonLinkColor, #6366f1) 55%, #c7d2fe);
  line-height: 1.55;
}

.alex-preview__sl-label {
  font-weight: 600;
  color: #6366f1;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.03em;
}

.alex-preview__sl-parent {
  font-weight: 500;
  color: #64748b;
  text-transform: none;
  font-size: 11px;
  letter-spacing: normal;
}

.alex-json-details {
  margin-bottom: 10px;
}

.alex-json-toggle {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: #4a90d9;
  padding: 0;
}

.alex-json-editor {
  width: 100%;
  box-sizing: border-box;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.4;
  padding: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  resize: vertical;
  min-height: 120px;
  margin-bottom: 10px;
}

.alex-chat__composer {
  flex-shrink: 0;
  padding: 20px 16px 22px;
  background: #fff;
  border-top: 1px solid #ebecf0;
}

.alex-chat--embedded .alex-chat__composer {
  padding: 20px 20px 5px;
}

.alex-chat__composer--disabled {
  opacity: 0.8;
}

.alex-composer-in {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.alex-attached {
  margin-bottom: 12px;
}

.alex-file-chip {
  display: flex;
  align-items: center;
  gap: 13px;
  border: 1px solid #ebecf0;
  border-radius: 12px;
  padding: 12px 14px;
  background: #fbfbfc;
}

.alex-file-chip__icon {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

.alex-file-chip__icon--pdf {
  background: #fcecec;
  color: #df5049;
}

.alex-file-chip__icon--doc {
  background: #eef4fd;
  color: #2563c9;
}

.alex-file-chip__icon--sheet {
  background: #edf7e8;
  color: #7cb342;
}

.alex-file-chip__icon--image {
  background: #f1ebf9;
  color: #6c3f9e;
}

.alex-file-chip__icon--text {
  background: #f1f3f4;
  color: #5f6368;
}

.alex-file-chip__meta {
  flex: 1;
  min-width: 0;
}

.alex-file-chip__name {
  font-size: 13px;
  font-weight: 600;
  color: #2b2d35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-file-chip__size {
  margin-top: 3px;
  font-size: 11.5px;
  color: #797c86;
}

.alex-file-chip__remove {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: #eceef1;
  color: #5f6368;
  font-size: 15px;
  line-height: 1;
  flex-shrink: 0;
}

.alex-file-chip__remove:hover:not(:disabled) {
  background: #e0e2e6;
}

.alex-file-chip__remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alex-composer-box {
  border: 1px solid #ebecf0;
  border-radius: 14px;
  padding: 8px 8px 8px 12px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #fff;
}

.alex-composer-box:focus-within {
  border-color: var(--buttonLinkColor, #4a90d9);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--buttonLinkColor, #4a90d9) 18%, transparent);
}

.alex-composer-box--drag {
  border-color: var(--buttonLinkColor, #4a90d9);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--buttonLinkColor, #4a90d9) 18%, transparent);
}

.alex-composer-box__file-input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.alex-attach-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #797c86;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}

.alex-attach-btn:hover:not(:disabled) {
  background: #f1f3f4;
  color: var(--buttonLinkColor, #4a90d9);
}

.alex-attach-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alex-attach-btn svg {
  width: 19px;
  height: 19px;
  fill: currentColor;
}

.alex-composer-textarea {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  max-height: 100px;
  color: #2b2d35;
  padding: 9px 0;
  background: transparent;
  min-height: 24px;
}

.alex-composer-textarea:disabled {
  color: #797c86;
  cursor: not-allowed;
}

.alex-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  background: var(--buttonLinkColor, #4a90d9);
  color: var(--buttonTextColor, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: filter 0.15s ease, opacity 0.15s ease;
}

.alex-send-btn:hover:not(:disabled) {
  filter: brightness(0.92);
}

.alex-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alex-send-btn svg {
  width: 17px;
  height: 17px;
  fill: currentColor;
}

.alex-send-btn__loader {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  opacity: 0.85;
  animation: alex-spin 0.7s linear infinite;
}

.alex-composer-hint {
  margin: 5px 0 0;
  font-size: 11.5px;
  color: #797c86;
  text-align: center;
}

.alex-btn {
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
}

.alex-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alex-btn--primary {
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn--send {
  width: 100%;
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn__loader {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  animation: alex-spin 0.7s linear infinite;
  vertical-align: -1px;
}

.alex-btn--apply {
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn--apply:hover:not(:disabled) {
  filter: brightness(0.92);
}

.alex-apply-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
}

@keyframes alex-spin {
  to {
    transform: rotate(360deg);
  }
}

.alex-published-lock {
  margin-top: 4px;
}

.alex-processing-notice {
  margin-top: 4px;
}

.alex-processing-notice__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #e3982f;
  background: #fdf4e6;
  border: 1px solid #f1ddb6;
  padding: 5px 11px;
  border-radius: 20px;
  margin-bottom: 12px;
}

.alex-processing-notice__tag--done {
  color: #0f766e;
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.alex-processing-notice__tag svg {
  width: 13px;
  height: 13px;
  fill: currentColor;
}

.alex-processing-notice__completed,
.alex-processing-notice__waiting {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.45;
}

.alex-processing-notice__completed {
  color: #0f766e;
  font-weight: 600;
}

.alex-processing-notice__waiting {
  color: #b45309;
}

.alex-msg-ai__body--processing {
  border-color: #f1ddb6;
  background: #fffdf8;
}

.alex-msg-ai__body--processing .alex-msg-ai__text {
  color: #334155;
}

.alex-bubble--processing {
  border-color: #f1ddb6;
  background: #fffdf8;
}

.alex-published-lock__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #e3982f;
  background: #fdf4e6;
  border: 1px solid #f1ddb6;
  padding: 5px 11px;
  border-radius: 20px;
  margin-bottom: 12px;
}

.alex-published-lock__tag svg {
  width: 13px;
  height: 13px;
  fill: #e3982f;
}

.alex-published-lock__action {
  margin-top: 16px;
  border: 1px solid #ebecf0;
  border-radius: 14px;
  overflow: hidden;
  background: #fbfbfc;
  padding: 16px;
}

.alex-published-lock__lead {
  font-size: 13px;
  color: #797c86;
  line-height: 1.5;
  margin: 0 0 14px;
}

.alex-published-lock__primary {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background: var(--buttonLinkColor, #2563c9);
  color: var(--buttonTextColor, #fff);
  border: none;
  border-radius: 10px;
  padding: 13px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.alex-published-lock__primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--buttonLinkColor, #2563c9) 88%, #000);
  color: var(--buttonTextColor, #fff);
}

.alex-published-lock__primary:disabled {
  opacity: 0.75;
  cursor: wait;
}

.alex-published-lock__primary svg {
  width: 18px;
  height: 18px;
  fill: var(--buttonTextColor, #fff);
}

.alex-published-lock__secondary {
  width: 100%;
  background: transparent;
  border: none;
  color: var(--buttonLinkColor, #2563c9);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  padding: 12px 0 2px;
}

.alex-published-lock__secondary:hover:not(:disabled) {
  text-decoration: underline;
}

.alex-published-lock__draft {
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid #ebecf0;
  border-radius: 12px;
  padding: 12px 14px;
  background: #fff;
  margin-top: 14px;
}

.alex-published-lock__draft-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #eef4fd;
  color: var(--buttonLinkColor, #2563c9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.alex-published-lock__draft-icon svg {
  width: 19px;
  height: 19px;
  fill: currentColor;
}

.alex-published-lock__draft-name {
  font-size: 13px;
  font-weight: 600;
  color: #2b2d35;
}

.alex-published-lock__draft-state {
  font-size: 11.5px;
  color: #7cb342;
  margin-top: 3px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/AIFormBuilder/ChatPanel.vue